// src/integrations/index.ts
/* eslint-disable @typescript-eslint/no-explicit-any */
// Any types are used for third-party integrations (GA, FB Pixel, GTM) where we extend the window object

import type { IntegrationConfig, ConsentState } from '../types'
import { debugLog } from '../utils/helpers'
import { initConsentMode, updateConsentMode } from './consent-mode'

// Track if consent mode was initialized
let consentModeInitialized = false

/**
 * Ensure Consent Mode is initialized before any Google tag
 */
function ensureConsentModeInitialized(): void {
  if (!consentModeInitialized && typeof window !== 'undefined') {
    initConsentMode({
      urlPassthrough: true,
      adsDataRedaction: true,
    })
    consentModeInitialized = true
  }
}

/**
 * Initialize Google Analytics
 */
function initGoogleAnalytics(measurementId: string): void {
  if (typeof window === 'undefined') return

  // Check if already initialized for this ID
  if (document.querySelector(`script[src*="gtag/js?id=${measurementId}"]`)) {
    debugLog('Google Analytics already initialized for', measurementId)
    return
  }

  debugLog('Initializing Google Analytics', measurementId)

  // CRITICAL: Ensure consent mode default is set BEFORE loading tag
  ensureConsentModeInitialized()

  // Check if gtag already exists
  if (!(window as any).gtag) {
    // Create gtag function
    ;(window as any).dataLayer = (window as any).dataLayer || []
    ;(window as any).gtag = function (...args: any[]) {
      ;(window as any).dataLayer.push(args)
    }
    ;(window as any).gtag('js', new Date())
  }

  // Load GA script (config will be called after consent update)
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtag/js?id=${measurementId}`
  document.head.appendChild(script)

  // Configure GA after script loads
  script.onload = () => {
    ;(window as any).gtag('config', measurementId, {
      anonymize_ip: true,
      cookie_flags: 'SameSite=None;Secure',
    })
    debugLog('Google Analytics configured')
  }

  debugLog('Google Analytics script loaded')
}

/**
 * Initialize Facebook Pixel
 */
function initFacebookPixel(pixelId: string): void {
  if (typeof window === 'undefined') return

  // Check if already initialized
  if (document.querySelector('script[src*="fbevents.js"]')) {
    debugLog('Facebook Pixel already initialized')
    return
  }

  debugLog('Initializing Facebook Pixel', pixelId)

  // Check if fbq already exists
  if (!(window as any).fbq) {
    // Create fbq function with queue
    const fbq: any = function (...args: any[]) {
      fbq.callMethod ? fbq.callMethod(...args) : fbq.queue.push(args)
    }

    if (!(window as any)._fbq) (window as any)._fbq = fbq
    fbq.push = fbq
    fbq.loaded = true
    fbq.version = '2.0'
    fbq.queue = []
    ;(window as any).fbq = fbq
  }

  // Initialize pixel
  ;(window as any).fbq('init', pixelId)
  ;(window as any).fbq('track', 'PageView')

  // Load FB Pixel script
  const script = document.createElement('script')
  script.async = true
  script.src = 'https://connect.facebook.net/en_US/fbevents.js'
  document.head.appendChild(script)

  debugLog('Facebook Pixel initialized')
}

/**
 * Initialize Google Tag Manager
 */
function initGoogleTagManager(containerId: string): void {
  if (typeof window === 'undefined') return

  // Check if already initialized for this container
  if (document.querySelector(`script[src*="gtm.js?id=${containerId}"]`)) {
    debugLog('Google Tag Manager already initialized for', containerId)
    return
  }

  debugLog('Initializing Google Tag Manager', containerId)

  // CRITICAL: Ensure consent mode default is set BEFORE loading GTM
  ensureConsentModeInitialized()

  // Create dataLayer
  ;(window as any).dataLayer = (window as any).dataLayer || []
  ;(window as any).dataLayer.push({
    'gtm.start': new Date().getTime(),
    event: 'gtm.js',
  })

  // Load GTM script
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.googletagmanager.com/gtm.js?id=${containerId}`
  document.head.appendChild(script)

  debugLog('Google Tag Manager initialized')
}

/**
 * Initialize Hotjar
 */
function initHotjar(hotjarId: string): void {
  if (typeof window === 'undefined') return

  // Check if already initialized for this ID
  if (document.querySelector(`script[src*="hotjar-${hotjarId}.js"]`)) {
    debugLog('Hotjar already initialized for', hotjarId)
    return
  }

  debugLog('Initializing Hotjar', hotjarId)

  // Create Hotjar function
  ;(window as any).hj =
    (window as any).hj ||
    function (...args: any[]) {
      ;((window as any).hj.q = (window as any).hj.q || []).push(args)
    }
  ;(window as any)._hjSettings = { hjid: hotjarId, hjsv: 6 }

  // Load Hotjar script
  const script = document.createElement('script')
  script.async = true
  script.src = `https://static.hotjar.com/c/hotjar-${hotjarId}.js?sv=6`
  document.head.appendChild(script)

  debugLog('Hotjar initialized')
}

/**
 * Initialize Microsoft Clarity
 */
function initClarity(clarityId: string): void {
  if (typeof window === 'undefined') return

  // Check if already initialized for this ID
  if (document.querySelector(`script[src*="clarity.ms/tag/${clarityId}"]`)) {
    debugLog('Microsoft Clarity already initialized for', clarityId)
    return
  }

  debugLog('Microsoft Clarity', clarityId)

  // Create Clarity function
  ;(window as any).clarity =
    (window as any).clarity ||
    function (...args: any[]) {
      ;((window as any).clarity.q = (window as any).clarity.q || []).push(args)
    }

  // Load Clarity script
  const script = document.createElement('script')
  script.async = true
  script.src = `https://www.clarity.ms/tag/${clarityId}`
  document.head.appendChild(script)

  debugLog('Microsoft Clarity initialized')
}

/**
 * Get category for integration
 */
function getIntegrationCategory(integration: keyof IntegrationConfig): string {
  const categoryMap: Record<keyof IntegrationConfig, string> = {
    googleAnalytics: 'analytics',
    facebookPixel: 'marketing',
    googleTagManager: 'analytics',
    hotjar: 'analytics',
    clarity: 'analytics',
  }

  return categoryMap[integration]
}

/**
 * Initialize integrations based on consent
 * CRITICAL: This is called AFTER user gives consent
 */
export function initIntegrations(userConfig: IntegrationConfig, consent: ConsentState): void {
  debugLog('Initializing integrations', { userConfig, consent })

  const integrations: IntegrationConfig = userConfig

  // Update consent mode with user choices
  updateConsentMode(consent)

  // Initialize each integration if consented
  if (integrations.googleAnalytics) {
    const category = getIntegrationCategory('googleAnalytics')
    if (consent[category]) {
      initGoogleAnalytics(integrations.googleAnalytics)
    } else {
      debugLog('Skipping Google Analytics - no consent for', category)
    }
  }

  if (integrations.facebookPixel) {
    const category = getIntegrationCategory('facebookPixel')
    if (consent[category]) {
      initFacebookPixel(integrations.facebookPixel)
    } else {
      debugLog('Skipping Facebook Pixel - no consent for', category)
    }
  }

  if (integrations.googleTagManager) {
    const category = getIntegrationCategory('googleTagManager')
    if (consent[category]) {
      initGoogleTagManager(integrations.googleTagManager)
    } else {
      debugLog('Skipping Google Tag Manager - no consent for', category)
    }
  }

  if (integrations.hotjar) {
    const category = getIntegrationCategory('hotjar')
    if (consent[category]) {
      initHotjar(integrations.hotjar)
    } else {
      debugLog('Skipping Hotjar - no consent for', category)
    }
  }

  if (integrations.clarity) {
    const category = getIntegrationCategory('clarity')
    if (consent[category]) {
      initClarity(integrations.clarity)
    } else {
      debugLog('Skipping Microsoft Clarity - no consent for', category)
    }
  }
}

/**
 * Check if any integration needs specific category
 */
export function hasIntegrationForCategory(
  integrations: IntegrationConfig,
  category: string
): boolean {
  for (const [key, value] of Object.entries(integrations)) {
    if (value && getIntegrationCategory(key as keyof IntegrationConfig) === category) {
      return true
    }
  }

  return false
}

/**
 * Initialize consent mode early (call this on page load before user interaction)
 * This sets default consent to 'denied' for all parameters
 */
export function initConsentModeEarly(): void {
  ensureConsentModeInitialized()
}
